// The Grain, Feed & Bedding sector, in one place.
//
// Two levels, the way the trade thinks about it: a group (Grain, Forage,
// Bedding…) and a specific type inside it (Feed Wheat, Barley Straw…). The
// group is stored in listings.sub_category so it can be filtered on directly;
// the specific type goes in details.type, which is free-form, so adding one
// never needs a database change.
//
// This is the single source of truth — the post-a-listing form and the browse
// page both read it, so a heading added here appears in both. Alex asked to be
// able to add more specific headings under each group himself in future; when
// that comes, this array becomes the seed for an admin-editable table and
// nothing else has to move.

export interface GrainFeedGroup {
  /** Stored in listings.sub_category. Stable — don't rename after go-live. */
  value: string;
  label: string;
  /** Heroicons outline name used on the browse filters. */
  icon: string;
  /** The specific headings offered under this group. */
  types: string[];
}

export const GRAIN_FEED_GROUPS: GrainFeedGroup[] = [
  {
    value: 'grain',
    label: 'Grain',
    icon: 'CircleStackIcon',
    types: [
      'Feed Wheat',
      'Milling Wheat',
      'Feed Barley',
      'Malting Barley',
      'Oats',
      'Oilseed Rape',
      'Beans',
      'Peas',
      'Maize Grain',
      'Triticale',
      'Rye',
      'Linseed',
      'Other Grain',
    ],
  },
  {
    value: 'forage',
    label: 'Forage',
    icon: 'SunIcon',
    types: [
      'Hay — Meadow',
      'Hay — Seed / Ryegrass',
      'Haylage',
      'Silage — Clamp',
      'Silage — Baled',
      'Maize Silage',
      'Wholecrop',
      'Lucerne',
      'Standing Grass / Grass Keep',
      'Other Forage',
    ],
  },
  {
    value: 'bedding',
    label: 'Bedding',
    icon: 'RectangleStackIcon',
    types: [
      'Barley Straw',
      'Wheat Straw',
      'Oat Straw',
      'Chopped Straw',
      'Wood Shavings',
      'Sawdust',
      'Paper / Recycled Bedding',
      'Sand',
      'Miscanthus',
      'Other Bedding',
    ],
  },
  {
    value: 'root-crops',
    label: 'Root Crops',
    icon: 'GlobeEuropeAfricaIcon',
    types: [
      'Fodder Beet',
      'Sugar Beet',
      'Potatoes',
      'Swedes',
      'Turnips',
      'Carrots',
      'Kale / Stubble Turnips',
      'Other Root Crop',
    ],
  },
  {
    value: 'straights',
    label: 'Straights',
    icon: 'BeakerIcon',
    types: [
      'Sugar Beet Pulp',
      'Soya Meal',
      'Rapeseed Meal',
      'Distillers Grains',
      'Brewers Grains',
      'Maize Gluten',
      'Wheat Feed',
      'Citrus Pulp',
      'Molasses',
      'Palm Kernel',
      'Other Straights',
    ],
  },
  {
    value: 'compound-feed',
    label: 'Compound Feed & Blends',
    icon: 'CubeIcon',
    types: [
      'Cattle Compound',
      'Dairy Cake',
      'Sheep Compound',
      'Pig Compound',
      'Poultry Compound',
      'Calf Rearer',
      'Coarse Mix',
      'Blend',
      'Other Compound Feed',
    ],
  },
  {
    value: 'supplements',
    label: 'Supplements & Minerals',
    icon: 'SparklesIcon',
    types: [
      'Mineral Buckets',
      'Boluses',
      'Licks',
      'Vitamins',
      'Salt',
      'Milk Powder',
      'Colostrum',
      'Other Supplement',
    ],
  },
];

/** Units a load of grain, forage or bedding is actually sold by. */
export const GRAIN_FEED_UNITS = [
  'Tonnes',
  'Bales — Big Square',
  'Bales — Round',
  'Bales — Small Square',
  'Bags — Bulk (600kg+)',
  'Bags — 25kg',
  'Loads',
  'Acres',
  'Litres',
];

/** The specific types under a group value, or [] if the group is unknown. */
export function grainFeedTypes(groupValue: string): string[] {
  return GRAIN_FEED_GROUPS.find((g) => g.value === groupValue)?.types ?? [];
}

/** "grain" -> "Grain". Falls back to the stored value so nothing renders blank. */
export function grainFeedGroupLabel(groupValue: string | null | undefined): string {
  if (!groupValue) return '';
  return GRAIN_FEED_GROUPS.find((g) => g.value === groupValue)?.label ?? groupValue;
}
